import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, ShoppingCart, TrendingUp, CalendarDays, Trophy } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

const COUNT_OPTIONS = [
  { value: 3, label: 'Last 3 months' },
  { value: 6, label: 'Last 6 months' },
  { value: 12, label: 'Last 12 months' },
];

const METRICS = [
  { key: 'orders', label: 'Orders', icon: ShoppingCart },
  { key: 'revenue', label: 'Revenue', icon: TrendingUp },
];

const fmtNumber = (n) => Number(n || 0).toLocaleString('en-US');
const fmtMoney = (n, sym) => `${sym}${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtPercent = (n) => `${(Number(n || 0) * 100).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
const currencySymbol = (cur) => (cur === 'TRY' ? '₺' : cur === 'USD' ? '$' : cur === 'EUR' ? '€' : `${cur || ''} `);

// Single-hue intensity ramp (blue-600). t in [0,1].
const cellColor = (t) => {
  if (!(t > 0)) return { background: '#f8fafc', color: '#cbd5e1' };
  const alpha = 0.12 + 0.88 * Math.sqrt(t);
  return {
    background: `rgba(37, 99, 235, ${alpha.toFixed(3)})`,
    color: alpha > 0.55 ? '#ffffff' : '#1e3a8a',
  };
};

const fmtCell = (v, metric) =>
  metric === 'revenue'
    ? v >= 1000 ? `${Math.round(v / 1000)}K` : fmtNumber(Math.round(v))
    : fmtNumber(Math.round(v));

export default function WeekdayOrderAnalysis() {
  const { selectedBrand } = useAuth();
  const [count, setCount] = useState(6);
  const [metric, setMetric] = useState('revenue');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = async () => {
    if (!selectedBrand?.id) return;
    try {
      setLoading(true);
      setError('');
      const url = new URL(`${API_BASE}/api/google-analytics/ga4/weekday-order-heatmap`);
      url.searchParams.set('months', String(count));
      const res = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || data?.detail || `HTTP ${res.status}`);
      }
      const data = await res.json().catch(() => ({}));
      setPayload(data);
      setLastUpdated(new Date());
    } catch (e) {
      setPayload(null);
      setError(e?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, count]);

  const periodsList = payload?.periods || [];
  const weekdaysList = payload?.weekdays || [];
  const sym = currencySymbol(payload?.currency);
  const isRevenue = metric === 'revenue';
  const avgField = isRevenue ? 'avgRevenue' : 'avgOrders';

  // Shared color scale across the month grid → months are directly comparable.
  const maxVal = useMemo(() => {
    let m = 0;
    for (const w of weekdaysList) {
      for (const p of periodsList) {
        const v = w.byPeriod?.[p.key]?.[metric] || 0;
        if (v > m) m = v;
      }
    }
    return m;
  }, [weekdaysList, periodsList, metric]);

  // Separate scale for the Avg/day column.
  const maxAvg = useMemo(() => {
    let m = 0;
    for (const w of weekdaysList) { const v = w[avgField] || 0; if (v > m) m = v; }
    return m;
  }, [weekdaysList, avgField]);

  // Best weekday (by the active metric's average) to highlight.
  const bestDow = useMemo(() => {
    let best = null;
    for (const w of weekdaysList) if (!best || (w[avgField] || 0) > (best[avgField] || 0)) best = w;
    return best?.dow;
  }, [weekdaysList, avgField]);

  const hasData = periodsList.length > 0 && weekdaysList.length > 0;

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        {/* Header */}
        <div className="p-6 bg-white border-b">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <img src="/GA4logoicon.svg" alt="GA4" className="w-12 h-12 object-contain" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Weekday Order Analysis</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Live from GA4 — which days of the week carry orders &amp; revenue, on average and month over month.
                  {selectedBrand?.name ? ` · ${selectedBrand.name}` : ''}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {/* Metric toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                {METRICS.map((m) => {
                  const Icon = m.icon;
                  const active = metric === m.key;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => setMetric(m.key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        active ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {m.label}
                    </button>
                  );
                })}
              </div>
              <select
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                className="px-4 py-2 bg-white border rounded-xl text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {COUNT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={fetchData}
                disabled={loading}
                className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
          )}

          {!selectedBrand?.id && (
            <div className="p-6 text-center text-gray-500">Please select a brand.</div>
          )}

          {/* Best-day callout */}
          {hasData && payload?.bestWeekday && (
            <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-600">En iyi gün (ortalama ciro)</div>
                <div className="text-lg font-bold text-gray-900">
                  {payload.bestWeekday.name} · {fmtMoney(payload.bestWeekday.avgRevenue, sym)}/gün
                  <span className="text-sm font-medium text-gray-500"> · ~{fmtNumber(Math.round(payload.bestWeekday.avgOrders))} sipariş/gün</span>
                </div>
              </div>
            </div>
          )}

          {/* Per-month summary strip */}
          {hasData && (
            <div className="overflow-x-auto">
              <div className="flex gap-3" style={{ minWidth: '100%' }}>
                {periodsList.map((p) => (
                  <div key={p.key} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 flex-1" style={{ minWidth: '140px' }}>
                    <div className="text-xs font-semibold text-gray-500">{p.label}</div>
                    <div className="mt-1 text-lg font-bold text-gray-900">
                      {isRevenue ? fmtMoney(p.revenue, sym) : `${fmtNumber(p.orders)} orders`}
                    </div>
                    <div className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                      <CalendarDays className="w-3.5 h-3.5 text-blue-500" />
                      En yoğun: {p.peakDowName} · CR {fmtPercent(p.conversionRate)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Heatmap: weekday rows × month columns + Avg/day column */}
          {hasData && (
            <div className="overflow-x-auto">
              <table className="border-separate" style={{ borderSpacing: '3px', minWidth: '100%' }}>
                <thead>
                  <tr>
                    <th className="text-left text-xs font-semibold text-gray-400 px-2 sticky left-0 bg-white z-10">Gün</th>
                    {periodsList.map((p) => (
                      <th key={p.key} className="text-center text-xs font-semibold text-gray-600 px-1 pb-1" style={{ minWidth: '64px' }}>
                        {p.label}
                      </th>
                    ))}
                    <th className="text-center text-xs font-bold text-amber-700 px-2 pb-1 border-l border-gray-200" style={{ minWidth: '84px' }}>
                      Ort./gün
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {weekdaysList.map((w) => {
                    const isBest = w.dow === bestDow;
                    const avgVal = w[avgField] || 0;
                    const avgStyle = cellColor(maxAvg > 0 ? avgVal / maxAvg : 0);
                    return (
                      <tr key={w.dow}>
                        <td className={`text-right text-xs pr-2 sticky left-0 bg-white whitespace-nowrap z-10 ${isBest ? 'font-bold text-amber-700' : 'font-medium text-gray-500'}`}>
                          {isBest && <Trophy className="w-3 h-3 inline mr-1 -mt-0.5 text-amber-500" />}{w.name}
                        </td>
                        {periodsList.map((p) => {
                          const cell = w.byPeriod?.[p.key] || {};
                          const cv = Number(cell[metric] || 0);
                          const t = maxVal > 0 ? cv / maxVal : 0;
                          const style = cellColor(t);
                          const tip =
                            `${w.name} · ${p.label}\n` +
                            `Orders: ${fmtNumber(cell.orders)}\n` +
                            `Revenue: ${fmtMoney(cell.revenue, sym)}\n` +
                            `Checkouts: ${fmtNumber(cell.checkouts)}\n` +
                            `Sessions: ${fmtNumber(cell.sessions)}\n` +
                            `Conversion: ${fmtPercent(cell.conversionRate)}`;
                          return (
                            <td key={p.key} title={tip} className="text-center font-medium rounded-md transition h-8 text-[11px]" style={{ ...style, minWidth: '64px' }}>
                              {cv > 0 ? fmtCell(cv, metric) : ''}
                            </td>
                          );
                        })}
                        <td
                          title={`${w.name} ortalaması (oluşum: ${w.occurrences} gün)\nOrt. ciro: ${fmtMoney(w.avgRevenue, sym)}/gün\nOrt. sipariş: ${fmtNumber(Math.round(w.avgOrders))}/gün\nToplam ciro: ${fmtMoney(w.totalRevenue, sym)}`}
                          className={`text-center rounded-md h-8 text-[11px] border-l border-gray-200 ${isBest ? 'font-extrabold' : 'font-semibold'}`}
                          style={{ ...avgStyle, minWidth: '84px' }}
                        >
                          {isRevenue ? fmtMoney(avgVal, sym) : fmtNumber(Math.round(avgVal))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-400">
                <span>Low</span>
                <div className="flex rounded-md overflow-hidden">
                  {[0.05, 0.25, 0.5, 0.75, 1].map((t) => (
                    <div key={t} className="w-6 h-3" style={{ background: cellColor(t).background }} />
                  ))}
                </div>
                <span>High</span>
                <span className="ml-2">
                  · {isRevenue ? `K = thousand ${sym.trim()}` : 'order count'} · grid renk ölçeği aylar arasında ortak ·
                  “Ort./gün” = o haftagünündeki bir günün ortalaması (oluşum sayısına bölünmüş)
                </span>
              </div>
            </div>
          )}

          {hasData && (
            <div className="text-xs text-gray-400">
              {payload?.startDate} – {payload?.endDate}
              {lastUpdated ? ` · Updated ${lastUpdated.toLocaleTimeString('en-US')}` : ''}
            </div>
          )}

          {!loading && !error && selectedBrand?.id && !hasData && (
            <div className="p-6 text-center text-gray-500">No weekday GA4 data found for this brand.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
